import React, { useRef, useState } from 'react';
import { Camera, Loader2, Upload, ShieldCheck, ShieldAlert, Shield, Save, Trash2, Sparkles, Flame, X, Pencil } from 'lucide-react';
import { Card, Btn } from './shared';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

/** Colour a confidence pill 1-10. */
const ConfidencePill = ({ value }) => {
  const v = Math.max(1, Math.min(10, value || 5));
  const [Icon, cls, label] = v >= 8
    ? [ShieldCheck, 'bg-[#BEFF38]/15 text-[#BEFF38] border-[#BEFF38]/40', 'High']
    : v >= 5
      ? [Shield,      'bg-sky-500/15 text-sky-300 border-sky-500/40',      'Medium']
      : [ShieldAlert, 'bg-[#ff7a1a]/15 text-[#ff7a1a] border-[#ff7a1a]/40', 'Low'];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] font-black uppercase tracking-[0.14em] ${cls}`}
      title={`Model confidence: ${v}/10`}
      data-testid="snap-confidence"
    >
      <Icon className="w-3 h-3" /> {label} · {v}/10
    </span>
  );
};

/** Small pill for a single macro. */
const MacroPill = ({ label, value, color }) => (
  <div className={`px-2.5 py-1 rounded-lg border border-zinc-800 bg-zinc-950 flex items-baseline gap-1 tabular-nums`}>
    <span className="text-white font-black text-sm">{value}</span>
    <span className="text-[9px] uppercase tracking-wider font-black" style={{ color }}>{label}</span>
  </div>
);

const FoodSnapCard = () => {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 7 * 1024 * 1024) {
      toast({ title: 'Image too large', description: 'Use an image under ~7MB', variant: 'destructive' });
      return;
    }
    const data = await readAsBase64(f);
    setPreview(data);
    setResult(null);
    setEditing(false);
  };

  const clearPhoto = () => {
    setPreview(null);
    setResult(null);
    setEditing(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const analyse = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const r = await api.foodSnap(preview, note);
      setResult(r);
      setEditItems((r.items || []).map((it) => ({ ...it })));
      window.dispatchEvent(new CustomEvent('freefitfuel:food-logged'));
      toast({
        title: 'Added to today',
        description: `${r.total_calories} kcal · P${r.total_protein_g} · C${r.total_carbs_g} · F${r.total_fats_g}. ${r.confidence < 6 ? 'Confidence low — tap Adjust to fix.' : ''}`,
      });
    } catch (err) {
      toast({ title: 'Vision failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const updateItem = (i, field, val) => {
    const next = editItems.slice();
    next[i] = { ...next[i], [field]: field === 'name' || field === 'portion' ? val : (Math.max(0, Number(val) || 0)) };
    setEditItems(next);
  };

  const removeSnap = async () => {
    if (!result?.id) return;
    try {
      await api.deleteFoodEntry(result.id);
      window.dispatchEvent(new CustomEvent('freefitfuel:food-logged'));
      setResult(null); setEditing(false);
      toast({ title: 'Removed', description: 'Snap deleted from today\u2019s log.' });
    } catch (err) {
      toast({ title: 'Could not remove', description: err?.message, variant: 'destructive' });
    }
  };

  const saveEdits = async () => {
    if (!result?.id) return;
    setSaving(true);
    try {
      await api.deleteFoodEntry(result.id);
      for (const it of editItems) {
        if (!it.name?.trim()) continue;
        await api.addManualFood({
          name: it.name.trim(),
          portion: it.portion || '1 serving',
          calories: it.calories || 0,
          protein_g: it.protein_g || 0,
          carbs_g: it.carbs_g || 0,
          fats_g: it.fats_g || 0,
        });
      }
      window.dispatchEvent(new CustomEvent('freefitfuel:food-logged'));
      const totals = editItems.reduce((a, it) => ({
        calories: a.calories + (it.calories || 0),
        protein_g: a.protein_g + (it.protein_g || 0),
        carbs_g: a.carbs_g + (it.carbs_g || 0),
        fats_g: a.fats_g + (it.fats_g || 0),
      }), { calories: 0, protein_g: 0, carbs_g: 0, fats_g: 0 });
      setResult({ ...result, items: editItems, total_calories: totals.calories, total_protein_g: totals.protein_g, total_carbs_g: totals.carbs_g, total_fats_g: totals.fats_g, id: null });
      setEditing(false);
      toast({ title: 'Saved', description: `Updated to ${totals.calories} kcal.` });
    } catch (err) {
      toast({ title: 'Could not save', description: err?.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Card title="Food snap" icon={Camera}>
      <div className="-mt-2 mb-4 flex items-center gap-2 text-[11px] text-[#aab1bf]">
        <Sparkles className="w-3.5 h-3.5 text-[#BEFF38] shrink-0" />
        <span>Reads your plate against UK portion refs · accounts for oils, dressings · gives a confidence score.</span>
      </div>

      <div className="grid sm:grid-cols-[minmax(0,240px)_1fr] gap-4">
        {/* --- LEFT: photo dropzone --- */}
        <div className="flex flex-col gap-2.5">
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} />
          <button
            type="button" onClick={() => fileRef.current?.click()}
            data-testid="snap-dropzone"
            className="group relative aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-zinc-950 to-black border border-dashed border-zinc-800 hover:border-[#ff7a1a]/70 transition-all"
          >
            {preview ? (
              <>
                <img src={preview} alt="meal" className="w-full h-full object-cover" />
                {/* subtle top gradient for the close button */}
                <div className="absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-black/60 to-transparent" />
                <span
                  role="button" tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); clearPhoto(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); clearPhoto(); } }}
                  className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 backdrop-blur text-white/90 hover:text-white hover:bg-black/80 flex items-center justify-center cursor-pointer"
                  aria-label="Clear photo"
                  data-testid="snap-clear"
                >
                  <X className="w-3.5 h-3.5" />
                </span>
              </>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
                <div className="w-11 h-11 rounded-full bg-[#ff7a1a]/15 border border-[#ff7a1a]/40 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Upload className="w-4 h-4 text-[#ff7a1a]" />
                </div>
                <p className="text-white text-sm font-bold">Tap to snap a meal</p>
                <p className="text-[10px] text-zinc-500 mt-1 leading-relaxed">or upload from your library · JPG / PNG · 7 MB max</p>
              </div>
            )}
          </button>
          <input
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder='e.g. "200g chicken, 1 tbsp oil"'
            className="bg-zinc-950 border border-zinc-800 focus:border-[#ff7a1a] focus:ring-2 focus:ring-[#ff7a1a]/20 rounded-lg px-3 py-2 text-white outline-none text-xs"
          />
          <Btn onClick={analyse} disabled={!preview || busy} data-testid="snap-analyse">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            {busy ? 'Analysing…' : 'Analyse & add'}
          </Btn>
        </div>

        {/* --- RIGHT: result --- */}
        <div>
          {!result ? (
            <div className="h-full min-h-[220px] rounded-2xl border border-dashed border-zinc-800 bg-zinc-950/50 flex flex-col items-center justify-center text-center p-6">
              <div className="w-11 h-11 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-3">
                <Flame className="w-5 h-5 text-zinc-600" />
              </div>
              <p className="text-zinc-400 text-sm font-semibold">Macros will appear here</p>
              <p className="text-[11px] text-zinc-600 mt-1 max-w-[240px]">Better lighting and a top-down angle boost accuracy. Add a note for portion.</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-zinc-800 bg-gradient-to-b from-zinc-950 to-black overflow-hidden" data-testid="snap-result">
              {/* Big kcal hero */}
              <div className="p-4 pb-3 border-b border-zinc-900 relative">
                <div className="flex items-baseline gap-2">
                  <span
                    className="font-black text-4xl text-[#ff7a1a] tabular-nums leading-none"
                    data-testid="snap-total-kcal"
                  >
                    {result.total_calories}
                  </span>
                  <span className="text-zinc-500 text-xs uppercase tracking-wider font-black">kcal</span>
                  <div className="ml-auto"><ConfidencePill value={result.confidence} /></div>
                </div>
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  <MacroPill label="Prot" value={`${result.total_protein_g}g`} color="#38bdf8" />
                  <MacroPill label="Carb" value={`${result.total_carbs_g}g`} color="#a78bfa" />
                  <MacroPill label="Fat"  value={`${result.total_fats_g}g`}  color="#fbbf24" />
                </div>
                {result.summary && (
                  <p className="mt-3 text-[11px] text-zinc-500 italic leading-relaxed">
                    <span className="text-zinc-400 not-italic">The AI says: </span>{result.summary}
                  </p>
                )}
              </div>

              {/* Items — view or edit */}
              {editing ? (
                <div className="p-3 space-y-2" data-testid="snap-edit-panel">
                  {editItems.map((it, i) => (
                    <div key={i} className="rounded-lg bg-zinc-900/60 border border-zinc-800 p-2.5 space-y-1.5">
                      <input
                        value={it.name || ''}
                        onChange={(e) => updateItem(i, 'name', e.target.value)}
                        className="w-full bg-transparent text-white text-sm font-bold outline-none"
                        placeholder="Food name"
                      />
                      <input
                        value={it.portion || ''}
                        onChange={(e) => updateItem(i, 'portion', e.target.value)}
                        placeholder="e.g. 200g grilled"
                        className="w-full bg-transparent text-[11px] text-zinc-500 outline-none"
                      />
                      <div className="grid grid-cols-4 gap-1.5">
                        {[['calories', 'kcal', '#ff7a1a'], ['protein_g', 'P', '#38bdf8'], ['carbs_g', 'C', '#a78bfa'], ['fats_g', 'F', '#fbbf24']].map(([f, label, color]) => (
                          <label key={f} className="flex flex-col">
                            <span className="text-[9px] uppercase tracking-wider font-black" style={{ color }}>{label}</span>
                            <input
                              type="number" inputMode="numeric" min="0"
                              value={it[f] ?? 0}
                              onChange={(e) => updateItem(i, f, e.target.value)}
                              className="mt-0.5 bg-zinc-950 border border-zinc-800 rounded px-1.5 py-1 text-white text-xs tabular-nums outline-none focus:border-[#BEFF38] focus:ring-1 focus:ring-[#BEFF38]/30"
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                Array.isArray(result.items) && result.items.length > 0 && (
                  <ul className="divide-y divide-zinc-900">
                    {result.items.map((it, i) => (
                      <li key={i} className="px-4 py-2.5 text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-white font-semibold truncate">{it.name}</span>
                          <span className="text-[#ff7a1a] font-black text-xs tabular-nums shrink-0">{it.calories}<span className="text-[9px] text-zinc-500 font-bold ml-0.5">kcal</span></span>
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-zinc-500 mt-1 tabular-nums">
                          <span className="truncate italic">{it.portion}</span>
                          <span className="shrink-0"><span className="text-sky-300">P {it.protein_g}</span> · <span className="text-violet-300">C {it.carbs_g}</span> · <span className="text-amber-300">F {it.fats_g}</span></span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )
              )}

              {/* Action bar */}
              <div className="border-t border-zinc-900 p-2.5 flex items-center gap-2">
                {editing ? (
                  <>
                    <button
                      type="button" onClick={saveEdits} disabled={saving}
                      data-testid="snap-edit-save"
                      className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#BEFF38] text-black font-black uppercase tracking-wider text-xs py-2 rounded-lg hover:brightness-95 disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      {saving ? 'Saving…' : 'Save changes'}
                    </button>
                    <button
                      type="button" onClick={() => { setEditing(false); setEditItems((result.items || []).map((it) => ({ ...it }))); }}
                      className="text-[10px] text-zinc-500 hover:text-white px-3 py-2 rounded-lg"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button" onClick={() => setEditing(true)}
                      data-testid="snap-edit-toggle"
                      className="flex-1 inline-flex items-center justify-center gap-1.5 bg-zinc-900 border border-zinc-800 text-zinc-200 hover:text-white hover:border-[#BEFF38]/50 font-black uppercase tracking-wider text-xs py-2 rounded-lg"
                    >
                      <Pencil className="w-3 h-3" /> Adjust macros
                    </button>
                    {result.id && (
                      <button
                        type="button" onClick={removeSnap}
                        data-testid="snap-delete"
                        title="Remove this snap from today"
                        className="h-8 w-8 shrink-0 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-[#ff7a1a] hover:border-[#ff7a1a]/40 inline-flex items-center justify-center"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default FoodSnapCard;
